import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Team,
  Pools,
  type DbPool,
  type Member,
  type Invite,
  type OveragePolicy,
} from "../../../lib/apiTyped";
import { useOrgSnapshot } from "../../../hooks/useOrgSnapshot";
import { useResource } from "../../../lib/useResource";
import { useApiCall } from "../../../lib/useToastError";
import { useNotifications } from "../../../components/shared/NotificationsProvider";
import { PageHeader } from "../../../components/AppLayout";
import { Card } from "../../../components/shared/Card";
import { Button } from "../../../components/shared/Button";
import { Spinner } from "../../../components/shared/Spinner";
import { StatusBadge } from "../../../components/shared/StatusBadge";
import { Modal } from "../../../components/shared/Modal";
import { FormInput } from "../../../components/shared/FormInput";
import { fmtDate, fmtNumber, fmtBytes } from "../../../lib/format";

const LIVE_SUB_STATUSES = new Set([
  "active",
  "trialing",
  "past_due",
  "incomplete",
]);

type RoleOption = "admin" | "member" | "external";
const ROLE_LABEL: Record<RoleOption | "owner", string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  external: "External",
};

export function OwnerTeam() {
  const { orgId, org, role } = useOrgSnapshot();
  const navigate = useNavigate();
  const { data, loading, refetch } = useResource(
    () => (orgId ? Team.list(orgId) : Promise.resolve(null)),
    [orgId],
  );
  const { run } = useApiCall();
  const { confirm } = useNotifications();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);

  if (loading || !orgId) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }
  if (role !== "owner" && role !== "admin") {
    return (
      <Card className="p-6">
        <p className="text-sm text-ink-2">
          Solo el owner o un admin pueden gestionar el equipo. Si necesitas
          cambios, pídeselo a alguno de ellos.
        </p>
      </Card>
    );
  }

  const members = (data?.members ?? []) as Member[];
  const invites = (data?.invites ?? []) as Invite[];
  const seatCount = data?.seatCount ?? 0;
  const seatsInUse = data?.seatsInUse ?? 0;
  const activeTier = data?.activeTier ?? null;
  const tier = data?.tier ?? null;
  const pool = data?.pool ?? null;

  const subscriptionStatus =
    (org as { subscriptionStatus?: string | null } | null)?.subscriptionStatus ??
    null;
  const subscriptionLive =
    !!subscriptionStatus && LIVE_SUB_STATUSES.has(subscriptionStatus);

  // Hard gate: a paid tier exists in Firestore as "admin intent" the
  // moment an admin pre-assigns it, but until the Stripe subscription is
  // live it must NEVER appear as activated. Trial bypasses this gate —
  // it's a real, usable plan that intentionally never touches Stripe.
  if (activeTier && activeTier !== "trial" && !subscriptionLive) {
    return (
      <>
        <PageHeader title="Team" />
        <Card className="p-6">
          <h2 className="text-lg font-black text-ink mb-1">
            Tu suscripción aún no está activa
          </h2>
          <p className="text-sm text-ink-2 mb-5">
            Tu organización tiene un plan asignado pendiente de activación.
            Activa el pago en Stripe para liberar los seats e invitar
            miembros.
          </p>
          <Button variant="primary" onClick={() => navigate("/billing")}>
            Activar
          </Button>
        </Card>
      </>
    );
  }

  async function revoke(uid: string) {
    if (!orgId) return;
    const ok = await confirm({
      title: "¿Eliminar miembro?",
      message:
        "Se elimina su cuenta de usuario y sus API keys. No se puede deshacer.",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => Team.remove(orgId, uid), {
      successMessage: "Miembro eliminado.",
    });
    await refetch();
  }
  async function setRole(uid: string, target: RoleOption) {
    if (!orgId) return;
    await run(() => Team.setRole(orgId, uid, target), {
      successMessage: `Rol actualizado a ${ROLE_LABEL[target]}.`,
    });
    await refetch();
  }
  async function cancelInvite(email: string) {
    if (!orgId) return;
    await run(() => Team.cancelInvite(orgId, email), {
      successMessage: "Invitación cancelada.",
    });
    await refetch();
  }

  const isOwner = role === "owner";

  return (
    <>
      <PageHeader
        title="Team"
        subtitle={
          activeTier
            ? `Seats usados: ${seatsInUse} / ${seatCount}. El pool de créditos y el almacenamiento son compartidos.`
            : "Asigna un plan a tu organización antes de invitar miembros."
        }
        action={
          activeTier ? (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              + Invitar miembro
            </Button>
          ) : null
        }
      />

      {tier && pool && (
        <Card className="p-5 mb-6">
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <span>
              <span className="text-ink-3">Tier:</span>{" "}
              <strong className="text-ink">{tier.name}</strong>
            </span>
            <span>
              <span className="text-ink-3">Pool de créditos / mes:</span>{" "}
              <strong className="text-ink">
                {fmtNumber(pool.monthlyCreditsLimit)}
              </strong>
            </span>
            <span>
              <span className="text-ink-3">Storage compartido:</span>{" "}
              <strong className="text-ink">
                {fmtBytes(pool.storageBytesLimit)}
              </strong>
            </span>
            <span>
              <span className="text-ink-3">Política de exceso:</span>{" "}
              <strong className="text-ink">
                {pool.overagePolicy === "billed" ? "Cobrado" : "Bloqueado"}
              </strong>
            </span>
            <span>
              <span className="text-ink-3">Reparto:</span>{" "}
              <strong className="text-ink">
                {pool.allocationMode === "manual" ? "Manual" : "Equitativo"}
              </strong>
            </span>
          </div>
          {pool.killSwitch && (
            <p className="mt-3 text-xs text-red-700">
              Pool agotado. Las llamadas al modelo están bloqueadas. Aumenta
              el plan o activa cobro por exceso.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPolicyOpen(true)}>
              Política y umbrales
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAllocOpen(true)}>
              Reparto del pool
            </Button>
            {isOwner && pool.killSwitch && (
              <Button
                variant="primary"
                size="sm"
                onClick={async () => {
                  if (!orgId) return;
                  await run(() => Pools.resetKillSwitch(orgId), {
                    successMessage: "Pool reabierto.",
                  });
                  await refetch();
                }}
              >
                Reabrir pool
              </Button>
            )}
          </div>
        </Card>
      )}

      {invites.length > 0 && (
        <Card className="p-5 mb-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
            Invitaciones pendientes
          </div>
          <ul className="divide-y divide-border">
            {invites.map((inv) => (
              <li
                key={inv.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div>
                  <div className="text-sm text-ink">{inv.email}</div>
                  <div className="text-xs text-ink-3">
                    {fmtDate(inv.invitedAt)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status="pending" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cancelInvite(inv.email)}
                  >
                    Cancelar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
          Miembros activos
        </div>
        <ul className="divide-y divide-border">
          {members.map((m) => {
            // Consumo y cap comparten reloj (la ventana de crédito), así que el
            // porcentaje solo pasa de 100 con overage real, no por arrastrar
            // consumo previo a la última renovación.
            const creditsUsed = Math.round(m.usage?.creditsUsedThisWindow ?? 0);
            const memberCap = computeMemberCap(pool, m.uid, members);
            const usagePct =
              memberCap > 0 ? Math.min(999, (creditsUsed / memberCap) * 100) : null;
            return (
              <li
                key={m.uid}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div>
                  <div className="text-sm text-ink">{m.email}</div>
                  <div className="text-xs text-ink-3 flex flex-wrap gap-2 mt-0.5">
                    <span>{ROLE_LABEL[m.role]}</span>
                    <span>·</span>
                    <span>
                      {fmtNumber(creditsUsed)} créditos en este periodo
                      {usagePct !== null && (
                        <span className="ml-1 text-ink-3">
                          ({usagePct.toFixed(1)}%)
                        </span>
                      )}
                    </span>
                    {memberCap > 0 && (
                      <>
                        <span>·</span>
                        <span>cap {fmtNumber(Math.round(memberCap))}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status="active" />
                  {m.role !== "owner" && (
                    <>
                      <RoleSelect
                        value={m.role as RoleOption}
                        canManageAdmin={isOwner}
                        onChange={(r) => setRole(m.uid, r)}
                      />
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => revoke(m.uid)}
                      >
                        Eliminar
                      </Button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      {inviteOpen && (
        <InviteMemberModal
          orgId={orgId}
          canInviteAdmin={isOwner}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            void refetch();
          }}
        />
      )}
      {policyOpen && pool && (
        <PolicyModal
          orgId={orgId}
          pool={pool}
          onClose={() => setPolicyOpen(false)}
          onSaved={async () => {
            setPolicyOpen(false);
            await refetch();
          }}
        />
      )}
      {allocOpen && pool && (
        <AllocationsModal
          orgId={orgId}
          pool={pool}
          members={members}
          onClose={() => setAllocOpen(false)}
          onSaved={async () => {
            setAllocOpen(false);
            await refetch();
          }}
        />
      )}
    </>
  );
}

function RoleSelect({
  value,
  canManageAdmin,
  onChange,
}: {
  value: RoleOption;
  canManageAdmin: boolean;
  onChange: (r: RoleOption) => void;
}) {
  return (
    <select
      className="text-xs rounded border border-border bg-white px-2 py-1"
      value={value}
      onChange={(e) => onChange(e.target.value as RoleOption)}
    >
      <option value="member">Member</option>
      <option value="external">External</option>
      <option value="admin" disabled={!canManageAdmin}>
        Admin
      </option>
    </select>
  );
}

function computeMemberCap(
  pool: DbPool | null,
  uid: string,
  members: Member[],
): number {
  if (!pool) return 0;
  const total = pool.monthlyCreditsLimit ?? 0;
  if (pool.allocationMode === "manual") {
    const a = pool.allocations?.[uid];
    if (a?.abs && a.abs >= 0) return a.abs;
    if (a?.pct && a.pct >= 0) return (total * Math.min(100, a.pct)) / 100;
    return (total * Math.max(0, Math.min(100, pool.perUserCapPct))) / 100;
  }
  const equal = members.length > 0 ? total / members.length : total;
  const ceiling =
    (total * Math.max(0, Math.min(100, pool.perUserCapPct))) / 100;
  return Math.min(equal, ceiling);
}

function InviteMemberModal({
  orgId,
  canInviteAdmin,
  onClose,
  onInvited,
}: {
  orgId: string;
  canInviteAdmin: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<RoleOption>("member");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function submit() {
    if (!email.trim() || busy) return;
    setBusy(true);
    const result = await run(() => Team.invite(orgId, email.trim(), role), {
      successMessage: "Invitación enviada. Pídele que inicie sesión con ese email.",
    });
    setBusy(false);
    if (result) onInvited();
  }

  return (
    <Modal onClose={onClose} maxWidth="26rem">
      <h3 className="text-lg font-black mb-1 pr-6 text-ink">Invitar miembro</h3>
      <p className="text-sm text-ink-3 mb-5">
        El miembro entrará con Google usando este email y compartirá el pool
        de créditos y el almacenamiento de la organización.
      </p>
      <FormInput
        label="Email"
        value={email}
        onChange={setEmail}
        placeholder="alice@empresa.com"
        type="email"
      />
      <label className="block mt-4 text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1.5">
        Rol
      </label>
      <select
        className="w-full rounded border border-border bg-white px-3 py-2 text-sm"
        value={role}
        onChange={(e) => setRole(e.target.value as RoleOption)}
      >
        <option value="member">Member — uso normal del pool</option>
        <option value="external">
          External — solo workspaces que se le asignen
        </option>
        <option value="admin" disabled={!canInviteAdmin}>
          Admin — puede gestionar miembros y pool
        </option>
      </select>
      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={submit} disabled={busy} fullWidth>
          {busy ? "Enviando…" : "Enviar invitación"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}

function PolicyModal({
  orgId,
  pool,
  onClose,
  onSaved,
}: {
  orgId: string;
  pool: DbPool;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [perUserCapPct, setPerUserCapPct] = useState(pool.perUserCapPct);
  const [overagePolicy, setOveragePolicy] = useState<OveragePolicy>(
    pool.overagePolicy,
  );
  const [overagePrice, setOveragePrice] = useState(
    pool.overageEurPerMillionCredits ?? 0,
  );
  const [thresholds, setThresholds] = useState<string>(
    (pool.alertThresholds ?? [75, 90, 100]).join(","),
  );
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function save() {
    if (busy) return;
    setBusy(true);
    const parsed = thresholds
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0 && n <= 200);
    const result = await run(
      () =>
        Pools.updatePolicy(orgId, {
          perUserCapPct,
          overagePolicy,
          overageEurPerMillionCredits: overagePrice,
          alertThresholds: parsed,
        }),
      { successMessage: "Política actualizada." },
    );
    setBusy(false);
    if (result) await onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="32rem">
      <h3 className="text-lg font-black mb-1 pr-6 text-ink">
        Política y umbrales del pool
      </h3>
      <p className="text-sm text-ink-3 mb-5">
        Configura el cap individual sobre el pool, qué pasa cuando se agota
        y cuándo recibir avisos.
      </p>

      <FormInput
        label="Cap individual (% del pool)"
        value={String(perUserCapPct)}
        onChange={(v) => setPerUserCapPct(Math.max(0, Math.min(100, Number(v) || 0)))}
        type="number"
      />
      <p className="mt-1 text-xs text-ink-3">
        Tope máximo que cualquier miembro puede consumir individualmente sobre
        el pool. 100 desactiva el cap individual.
      </p>

      <label className="block mt-5 text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1.5">
        Política cuando se agota
      </label>
      <select
        className="w-full rounded border border-border bg-white px-3 py-2 text-sm"
        value={overagePolicy}
        onChange={(e) => setOveragePolicy(e.target.value as OveragePolicy)}
      >
        <option value="block">Bloquear (recomendado)</option>
        <option value="billed">Cobrar exceso</option>
      </select>

      {overagePolicy === "billed" && (
        <>
          <FormInput
            label="Precio por millón de créditos extra (EUR)"
            value={String(overagePrice)}
            onChange={(v) => setOveragePrice(Math.max(0, Number(v) || 0))}
            type="number"
          />
          <p className="mt-1 text-xs text-ink-3">
            Se factura como meter de Stripe `formular_tokens_overage`.
          </p>
        </>
      )}

      <FormInput
        label="Avisos al cruzar (% separados por coma)"
        value={thresholds}
        onChange={setThresholds}
        placeholder="75, 90, 100"
      />

      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={save} disabled={busy} fullWidth>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}

function AllocationsModal({
  orgId,
  pool,
  members,
  onClose,
  onSaved,
}: {
  orgId: string;
  pool: DbPool;
  members: Member[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [mode, setMode] = useState<"equal" | "manual">(pool.allocationMode);
  const [allocations, setAllocations] = useState<
    Record<string, { pct?: number; abs?: number }>
  >(pool.allocations ?? {});
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();
  useEffect(() => {
    setAllocations(pool.allocations ?? {});
    setMode(pool.allocationMode);
  }, [pool]);

  function setPct(uid: string, pct: number) {
    setAllocations((prev) => ({
      ...prev,
      [uid]: { ...(prev[uid] ?? {}), pct, abs: undefined },
    }));
  }

  const totalPct = useMemo(
    () =>
      Object.values(allocations).reduce(
        (acc, a) => acc + (typeof a.pct === "number" ? a.pct : 0),
        0,
      ),
    [allocations],
  );

  async function save() {
    if (busy) return;
    if (mode === "manual" && totalPct > 100.0001) {
      alert(`Las cuotas suman ${totalPct.toFixed(1)}% — no puede pasar de 100%.`);
      return;
    }
    setBusy(true);
    const result = await run(
      () =>
        Pools.updateAllocations(
          orgId,
          mode,
          mode === "manual" ? allocations : undefined,
        ),
      { successMessage: "Reparto guardado." },
    );
    setBusy(false);
    if (result) await onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="36rem">
      <h3 className="text-lg font-black mb-1 pr-6 text-ink">
        Reparto del pool
      </h3>
      <p className="text-sm text-ink-3 mb-5">
        En modo equitativo, cada miembro tiene el mismo cap. En modo manual
        puedes asignar un porcentaje del pool a quien quieras.
      </p>

      <div className="flex gap-2 mb-5">
        <Button
          variant={mode === "equal" ? "primary" : "ghost"}
          size="sm"
          onClick={() => setMode("equal")}
        >
          Equitativo
        </Button>
        <Button
          variant={mode === "manual" ? "primary" : "ghost"}
          size="sm"
          onClick={() => setMode("manual")}
        >
          Manual
        </Button>
      </div>

      {mode === "manual" && (
        <>
          <ul className="divide-y divide-border">
            {members.map((m) => (
              <li key={m.uid} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink truncate">{m.email}</div>
                  <div className="text-xs text-ink-3">
                    {ROLE_LABEL[m.role]}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="w-20 rounded border border-border bg-white px-2 py-1 text-sm"
                    value={allocations[m.uid]?.pct ?? 0}
                    onChange={(e) =>
                      setPct(m.uid, Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                    }
                  />
                  <span className="text-xs text-ink-3">%</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-ink-3">
            Suma actual: <strong>{totalPct.toFixed(1)}%</strong> (no puede
            pasar de 100%).
          </div>
        </>
      )}

      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={save} disabled={busy} fullWidth>
          {busy ? "Guardando…" : "Guardar reparto"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}
